"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LinkExportCorrectionView = exports.ConnectorConfigEntryView = exports.ConnectorConfigInstallBody = exports.CONNECTOR_CONFIG_RANGES = exports.ConnectorConfigKey = exports.CONNECTOR_CONFIG_KEYS = exports.ReauthenticateBody = exports.ExportCorrectionApprovalResponse = exports.ExportCorrectionApprovalBody = exports.ExportCorrectionAck = exports.ExportCorrectionDiagnosticAck = exports.ExportCorrectionObservationAck = exports.ExportCorrectionResultAck = exports.CorrectionDisposition = exports.ExportCorrectionDiagnosticLine = exports.ExportCorrectionDiagnosticCode = exports.EXPORT_CORRECTION_DIAGNOSTIC_CODES = exports.ExportCorrectionObservationLine = exports.CorrectionExportStatus = exports.ExportCorrectionResultLine = exports.ExportCorrectionPosition = exports.CorrectionJson = exports.ExportCorrectionLineOutcome = exports.ExportCorrectionOutcome = exports.EXPORT_CORRECTION_OUTCOMES = exports.ExportCorrectionCommandV1 = exports.CorrectionSha256 = exports.CorrectionUuid = exports.CorrectionTimestamp = exports.EXPORT_CORRECTION_VALUES = exports.LINK_CORRECTION_HASH_PREFIX_BYTES = exports.LINK_CORRECTION_MAX_POSITIONS = exports.LINK_CORRECTION_MAX_LINES = exports.LINK_CORRECTION_MAX_ACKS = exports.LINK_CORRECTION_MAX_COMMANDS = exports.EXPORT_CORRECTION_SCHEMA = exports.EXPORT_CORRECTION_CAPABILITY = void 0;
const zod_1 = require("zod");
/**
 * VL-400 — correct-and-requeue a Link export refused for an invalid
 * `add_method` (design option A, revision 6 + amendment 1).
 *
 * A tenant administrator who holds the explicit `connectors.export.repair`
 * grant approves ONE correction for ONE failed, unnumbered receipt export of
 * ONE store's Link. Back Office stores the approval and hands it to the Link
 * in the answer to its next status report; the Link applies it under its own
 * row lock, as an overlay on that export row only (the till's record is never
 * rewritten), and reports what it did in a later report.
 *
 * ------------------------------------------------------------------
 * WIRE SHAPE: OUTER ARRAYS LOOSE, ELEMENTS STRICT
 * ------------------------------------------------------------------
 * The status report and its answer carry the new arrays as
 * `z.array(z.unknown()).max(<bound>)`. The whole-message schema checks only
 * the bound, so one malformed element can never make the receiver throw away
 * the whole message (and with it every retry request and every other
 * element). Each element is then parsed on its own, against the strict
 * schemas below, in a `try` per element (design §6).
 *
 * Every strict schema refuses an unknown key. A new field arrives only under
 * a new `schema` literal, which Back Office sends only to a Link advertising
 * it (`capabilities`).
 *
 * ------------------------------------------------------------------
 * NOTHING HERE IS AUTHORITY
 * ------------------------------------------------------------------
 * The Link's device token authenticates the status channel only. A command is
 * executed only as fingerprinted; the fingerprint is recomputed by the Link.
 * `contradiction` and `orphaned` dispositions are delivery retirement only —
 * they never approve, recognise or reconcile anything.
 */
/** The capability a Link advertises in `LinkStatusReport.capabilities`. */
exports.EXPORT_CORRECTION_CAPABILITY = 'export_correction_v1';
exports.EXPORT_CORRECTION_SCHEMA = 'export_correction_v1';
/** Design constants (§6.1, §8.1) — bounds, not owner windows. */
exports.LINK_CORRECTION_MAX_COMMANDS = 50;
exports.LINK_CORRECTION_MAX_ACKS = 200;
exports.LINK_CORRECTION_MAX_LINES = 50;
/** Most positions one receipt can carry in a result line. */
exports.LINK_CORRECTION_MAX_POSITIONS = 1000;
/** Bounded hash input of a diagnostic payload (§7.1.4). */
exports.LINK_CORRECTION_HASH_PREFIX_BYTES = 65_536;
/** The `add_method` values the store system knows (VL-183). */
exports.EXPORT_CORRECTION_VALUES = [0, 1, 2];
const CorrectionId = zod_1.z.string().min(1).max(128);
/** RFC 3339 UTC with `Z` and exactly three fractional digits — `Date.toISOString()`. */
exports.CorrectionTimestamp = zod_1.z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/, 'an RFC 3339 UTC time with milliseconds')
    .refine((value) => {
    const at = Date.parse(value);
    return Number.isFinite(at) && new Date(at).toISOString() === value;
}, 'a real instant');
/** A lower-case canonical UUID. */
exports.CorrectionUuid = zod_1.z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, 'a lower-case UUID');
exports.CorrectionSha256 = zod_1.z.string().regex(/^[0-9a-f]{64}$/, '64 lower-case hex digits');
/**
 * `export_correction_v1` (§6.3). `value` is any safe integer here; whether it
 * is one the store system knows is the Link's final check E7 (`invalid_value`).
 */
exports.ExportCorrectionCommandV1 = zod_1.z
    .object({
    schema: zod_1.z.literal(exports.EXPORT_CORRECTION_SCHEMA),
    requestId: exports.CorrectionUuid,
    terminalId: CorrectionId,
    locationId: CorrectionId,
    sourceId: CorrectionId,
    exportId: CorrectionId,
    documentId: CorrectionId,
    kind: zod_1.z.literal('receipt'),
    value: zod_1.z.number().int().refine(Number.isSafeInteger, 'a safe integer'),
    reason: zod_1.z.string().min(10).max(500),
    approverUserId: CorrectionId,
    approverName: zod_1.z.string().min(1).max(128),
    approvedAt: exports.CorrectionTimestamp,
    validUntil: exports.CorrectionTimestamp,
    fingerprint: exports.CorrectionSha256,
})
    .strict()
    .refine((command) => command.approvedAt < command.validUntil, {
    message: 'approvedAt must be before validUntil',
    path: ['validUntil'],
});
/**
 * Final outcomes of the Link's transaction `T` (§7.4), stored durably on the
 * Link and reported. `command_conflict` is a wire-only line (never stored).
 */
exports.EXPORT_CORRECTION_OUTCOMES = [
    'corrected',
    'wrong_target',
    'expired',
    'not_owner',
    'already_corrected',
    'not_eligible:status',
    'not_eligible:numbered',
    'not_eligible:no_invalid_method',
    'invalid_value',
];
exports.ExportCorrectionOutcome = zod_1.z.enum(exports.EXPORT_CORRECTION_OUTCOMES);
exports.ExportCorrectionLineOutcome = zod_1.z.enum([
    ...exports.EXPORT_CORRECTION_OUTCOMES,
    'command_conflict',
]);
exports.CorrectionJson = zod_1.z.lazy(() => zod_1.z.union([
    zod_1.z.null(),
    zod_1.z.boolean(),
    zod_1.z.number(),
    zod_1.z.string(),
    zod_1.z.array(exports.CorrectionJson),
    zod_1.z.record(exports.CorrectionJson),
]));
/** One corrected line: its 1-based position, the till's value, the chosen one. */
exports.ExportCorrectionPosition = zod_1.z
    .object({
    line: zod_1.z.number().int().min(1),
    old: exports.CorrectionJson,
    new: zod_1.z.number().int().min(0).max(2),
})
    .strict();
/**
 * A result line (§8.1). `corrected` carries its positions and the Link-clock
 * `appliedAt`; every other outcome carries neither (empty, null).
 */
exports.ExportCorrectionResultLine = zod_1.z
    .object({
    requestId: exports.CorrectionUuid,
    fingerprint: exports.CorrectionSha256,
    outcome: exports.ExportCorrectionLineOutcome,
    positions: zod_1.z.array(exports.ExportCorrectionPosition).max(exports.LINK_CORRECTION_MAX_POSITIONS).default([]),
    appliedAt: exports.CorrectionTimestamp.nullable().default(null),
})
    .strict()
    .refine((line) => line.outcome === 'corrected'
    ? line.appliedAt !== null && line.positions.length > 0
    : line.appliedAt === null && line.positions.length === 0, 'a corrected line carries positions and appliedAt; any other carries neither');
exports.CorrectionExportStatus = zod_1.z.enum(['pending', 'failed', 'conflict', 'written']);
/** An observation line (§8.1): the state of a corrected export as the Link read it. */
exports.ExportCorrectionObservationLine = zod_1.z
    .object({
    requestId: exports.CorrectionUuid,
    fingerprint: exports.CorrectionSha256,
    exportId: CorrectionId,
    exportStatus: exports.CorrectionExportStatus,
    writtenAt: exports.CorrectionTimestamp.nullable(),
    overlayPresent: zod_1.z.literal(true),
    observedAt: exports.CorrectionTimestamp,
})
    .strict();
exports.EXPORT_CORRECTION_DIAGNOSTIC_CODES = [
    'schema_version',
    'unknown_field',
    'type',
    'range',
    'timestamp',
    'fingerprint_mismatch',
    'wrong_link',
];
exports.ExportCorrectionDiagnosticCode = zod_1.z.enum(exports.EXPORT_CORRECTION_DIAGNOSTIC_CODES);
/** A diagnostic line (§7.1.2, §8.4): a command element the Link could not take. */
exports.ExportCorrectionDiagnosticLine = zod_1.z
    .object({
    id: exports.CorrectionUuid,
    requestId: exports.CorrectionUuid,
    code: exports.ExportCorrectionDiagnosticCode,
    receivedFingerprint: exports.CorrectionSha256.nullable(),
    payloadSha256: exports.CorrectionSha256,
    seenCount: zod_1.z.number().int().min(1),
    firstSeenAt: exports.CorrectionTimestamp,
    lastSeenAt: exports.CorrectionTimestamp,
})
    .strict();
exports.CorrectionDisposition = zod_1.z.enum(['stored', 'duplicate', 'contradiction', 'orphaned']);
/** Exact ACKs (§8.3.2, §8.4.2, §8.5.6, §8.7.1), built only after a confirmed COMMIT. */
exports.ExportCorrectionResultAck = zod_1.z
    .object({
    kind: zod_1.z.literal('result'),
    requestId: exports.CorrectionUuid,
    fingerprint: exports.CorrectionSha256,
    outcome: exports.ExportCorrectionLineOutcome,
    positionsSha256: exports.CorrectionSha256,
    appliedAt: exports.CorrectionTimestamp.nullable(),
    disposition: exports.CorrectionDisposition,
})
    .strict();
exports.ExportCorrectionObservationAck = zod_1.z
    .object({
    kind: zod_1.z.literal('observation'),
    requestId: exports.CorrectionUuid,
    fingerprint: exports.CorrectionSha256,
    exportId: CorrectionId,
    exportStatus: exports.CorrectionExportStatus,
    writtenAt: exports.CorrectionTimestamp.nullable(),
    disposition: exports.CorrectionDisposition,
})
    .strict();
exports.ExportCorrectionDiagnosticAck = zod_1.z
    .object({
    kind: zod_1.z.literal('diagnostic'),
    id: exports.CorrectionUuid,
    seenCount: zod_1.z.number().int().min(1),
    disposition: zod_1.z.enum(['stored', 'duplicate']),
})
    .strict();
exports.ExportCorrectionAck = zod_1.z.discriminatedUnion('kind', [
    exports.ExportCorrectionResultAck,
    exports.ExportCorrectionObservationAck,
    exports.ExportCorrectionDiagnosticAck,
]);
// ------------------------------------------------------------------
// Operator surface
// ------------------------------------------------------------------
/** `POST …/link-status/exports/:exportId/correction` — only `value` and `reason` come from the body. */
exports.ExportCorrectionApprovalBody = zod_1.z
    .object({
    value: zod_1.z.unknown(),
    reason: zod_1.z.unknown(),
})
    .strict();
exports.ExportCorrectionApprovalResponse = zod_1.z.object({
    requestId: exports.CorrectionUuid,
    approvedAt: exports.CorrectionTimestamp,
    validUntil: exports.CorrectionTimestamp,
});
/** `POST /auth/reauthenticate` (step-up): the password only. */
exports.ReauthenticateBody = zod_1.z.object({ password: zod_1.z.string().min(1).max(1024) }).strict();
exports.CONNECTOR_CONFIG_KEYS = [
    'connectors.export.repair.freshAuthMaxAgeSeconds',
    'connectors.export.correction.approvalMaxAgeSeconds',
];
exports.ConnectorConfigKey = zod_1.z.enum(exports.CONNECTOR_CONFIG_KEYS);
/**
 * Inclusive ranges. The owner set QV's range (amendment 1, A1); the fresh-login
 * range is the design's proposal. NEITHER has a default: a key exists only
 * once installed by an explicit, audited act.
 */
exports.CONNECTOR_CONFIG_RANGES = {
    'connectors.export.repair.freshAuthMaxAgeSeconds': { min: 60, max: 900 },
    'connectors.export.correction.approvalMaxAgeSeconds': { min: 300, max: 604_800 },
};
exports.ConnectorConfigInstallBody = zod_1.z.object({ value: zod_1.z.unknown() }).strict();
exports.ConnectorConfigEntryView = zod_1.z.object({
    key: exports.ConnectorConfigKey,
    value: zod_1.z.number().int().nullable(),
    /** `missing` or `invalid` both refuse: there is no fallback. */
    state: zod_1.z.enum(['installed', 'missing', 'invalid']),
    installedBy: zod_1.z.string().nullable(),
    installedAt: zod_1.z.string().datetime().nullable(),
    min: zod_1.z.number().int(),
    max: zod_1.z.number().int(),
});
/**
 * The four independent facets of one correction request (§10). There is no
 * single monotonic status: each facet is its own assertion.
 */
exports.LinkExportCorrectionView = zod_1.z.object({
    requestId: zod_1.z.string(),
    terminalId: zod_1.z.string(),
    exportId: zod_1.z.string(),
    sourceId: zod_1.z.string(),
    documentId: zod_1.z.string(),
    value: zod_1.z.number().int(),
    reason: zod_1.z.string(),
    approverName: zod_1.z.string(),
    approvedAt: zod_1.z.string().datetime(),
    validUntil: zod_1.z.string().datetime(),
    lifecycle: zod_1.z.object({
        handoffAttempts: zod_1.z.number().int().min(0),
        lastHandoffAt: zod_1.z.string().datetime().nullable(),
        stop: zod_1.z
            .object({
            kind: zod_1.z.enum(['withdrawn_before_handoff', 'handoff_stopped']),
            code: zod_1.z.string(),
            at: zod_1.z.string().datetime(),
        })
            .nullable(),
        /** Derived from fresh reads, never an event: "no further handoff while <check> fails". */
        blockedBy: zod_1.z.string().nullable(),
    }),
    outcome: zod_1.z.object({
        state: zod_1.z.enum(['none', 'applied', 'refused']),
        outcome: exports.ExportCorrectionOutcome.nullable(),
        appliedAt: zod_1.z.string().nullable(),
        receivedAt: zod_1.z.string().datetime().nullable(),
        positions: zod_1.z.array(exports.ExportCorrectionPosition),
        /** Received lines that conflict with Back Office's record (kept as evidence). */
        conflicts: zod_1.z.number().int().min(0),
        afterHandoffStop: zod_1.z.boolean(),
    }),
    history: zod_1.z.array(zod_1.z.object({ writtenAt: zod_1.z.string().datetime() })),
    custody: zod_1.z.object({
        state: zod_1.z.enum(['unknown', 'pending', 'failed', 'conflict', 'written']),
        reason: zod_1.z
            .enum(['never_observed', 'inconsistent', 'not_orderable', 'clock_behind', 'aged_out'])
            .nullable(),
        receivedAt: zod_1.z.string().datetime().nullable(),
        observedAt: zod_1.z.string().datetime().nullable(),
        writtenAt: zod_1.z.string().datetime().nullable(),
    }),
    lastAccepted: zod_1.z
        .object({
        exportStatus: exports.CorrectionExportStatus,
        writtenAt: zod_1.z.string().datetime().nullable(),
        observedAt: zod_1.z.string().datetime(),
        receivedAt: zod_1.z.string().datetime(),
        ageMs: zod_1.z.number().int(),
    })
        .nullable(),
});
//# sourceMappingURL=link-export-correction.js.map